const GRAPH_SCOPE = ["Sites.Read.All"];

export class SPAccessError extends Error {
  constructor(code, message) {
    super(message || code);
    this.name = "SPAccessError";
    this.code = code;
  }
}

function checkGraphError(data) {
  const code = data?.error?.code;
  if (!code) return;
  if (code === "accessDenied" || code === "itemNotFound") {
    throw new SPAccessError(code, data.error.message);
  }
  // Any other Graph error (e.g. invalidRequest for a malformed/non-SharePoint
  // URL) must still surface as a failure -- otherwise the error payload gets
  // silently treated as valid data and the caller never learns the link was
  // bad, instead of showing "Please provide a valid Folder/File link".
  throw new Error(data.error.message || code);
}

// Legacy SharePoint "Team Site" links (e.g. https://sites.ey.com/teams/<name>/...)
// are not supported -- only modern "/sites/" links and other non-team-site
// share links are accepted. Checked against the URL shape alone so an
// unsupported link is rejected before spending a token/API round-trip on it.
const LEGACY_TEAMS_SITE_PATH = /^\/teams\//i;

function isLegacyTeamsSiteUrl(sharePointURL) {
  try {
    return LEGACY_TEAMS_SITE_PATH.test(new URL(sharePointURL).pathname);
  } catch {
    return false;
  }
}

// Encodes a SharePoint/OneDrive URL into the token format required by the
// Graph "Shares" API. See: https://learn.microsoft.com/en-us/graph/api/shares-get
function encodeSharingUrl(url) {
  const base64 = btoa(unescape(encodeURIComponent(url)));
  return (
    "u!" + base64.replace(/=+$/, "").replace(/\//g, "_").replace(/\+/g, "-")
  );
}

// Resolves any accessible SharePoint/OneDrive link (direct deep link,
// "AllItems.aspx" view link, ":f:/r:" share link, root-site or sub-site link,
// personal OneDrive link, etc.) directly to its DriveItem via the Graph
// Shares API, instead of parsing site/library/folder structure from the URL
// text (which breaks for any link that doesn't follow the exact
// "/sites/{site}/{library}/{folder}" shape).
async function resolveSharedItem(sharePointURL, accessToken) {
  const encodedUrl = encodeSharingUrl(sharePointURL);
  const res = await fetch(
    `https://graph.microsoft.com/v1.0/shares/${encodedUrl}/driveItem`,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        Prefer: "redeemSharingLinkIfNecessary",
      },
    }
  );

  const data = await res.json();
  checkGraphError(data);

  return data;
}

// Caps how many "list children" calls may be in flight at once, within a
// single tree depth/level (see getAllFilesInFolder below), so wide trees
// are fetched in parallel for speed without flooding Graph enough to
// trigger throttling (HTTP 429).
const RECURSION_CONCURRENCY_LIMIT = 5;

// Runs `task(item)` over `items` with at most `limit` concurrently in
// flight, returning results in the same order as `items`. Unlike a shared
// semaphore reused across recursive levels, this pool is only ever used
// for one flat, static batch of items at a time (see the breadth-first
// traversal below) -- a task never tries to acquire another slot from the
// same pool while still holding one, so there's no risk of the circular
// wait that caused a deadlock in an earlier (nested-acquisition) version
// of this concurrency limiter.
async function mapWithConcurrencyLimit(items, limit, task) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex++;
      results[currentIndex] = await task(items[currentIndex], currentIndex);
    }
  }

  const workerCount = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workerCount }, () => worker()));

  return results;
}

// Fetches all children of a single folder (handling pagination), pushing
// any files found into `files` and returning the ids of any subfolders
// found, so the caller can continue the breadth-first traversal.
async function listFolderChildren(driveId, folderId, token, files) {
  const subFolderIds = [];
  let url = `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${folderId}/children`;

  while (url) {
    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json();
    checkGraphError(data);

    for (const item of data.value) {
      if (item.folder) {
        subFolderIds.push(item.id);
      } else {
        files.push(item);
      }
    }

    url = data["@odata.nextLink"] || null;
  }

  return subFolderIds;
}

async function getAllFilesInFolder(driveId, folderId, token) {
  const files = [];

  // Check if the folderId is a file or folder
  const folderRes = await fetch(
    `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${folderId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const folderData = await folderRes.json();
  checkGraphError(folderData);

  if (folderData.file) {
    // If it's a single file, add it directly to the files array
    files.push(folderData);
  } else if (folderData.folder) {
    // Breadth-first traversal: fetch all folders at the current depth
    // concurrently (bounded by RECURSION_CONCURRENCY_LIMIT), collect the
    // subfolders they contain, then move on to that next depth as a fresh
    // batch. Processing strictly depth-by-depth (rather than recursing
    // immediately into each subfolder) means a batch is always fully
    // drained before the next one starts, so no task ever waits on a slot
    // held up by another task in the same batch.
    let currentLevelIds = [folderId];
    while (currentLevelIds.length > 0) {
      const subFolderIdsPerFolder = await mapWithConcurrencyLimit(
        currentLevelIds,
        RECURSION_CONCURRENCY_LIMIT,
        (id) => listFolderChildren(driveId, id, token, files)
      );
      currentLevelIds = subFolderIdsPerFolder.flat();
    }
  }

  return files;
}
// Resolves a SharePoint/OneDrive link to its root item and site, without
// walking its folder tree. This is the "is this link valid/accessible"
// check -- cheap (one Graph call plus the folder/file type check), so it's
// safe to run as soon as a URL is pasted. Call fetchSharePointFiles()
// separately (e.g. only once the user clicks "Proceed") to actually walk
// the folder tree and enumerate every file, since that can be expensive for
// large folders/libraries and shouldn't block just confirming the link works.
export const resolveSharePointLink = async (sharePointURL, instance, accounts) => {
  if (isLegacyTeamsSiteUrl(sharePointURL)) {
    throw new Error("Legacy Teams site links are not supported");
  }

  const account = accounts[0];

  const response = await instance.acquireTokenSilent({
    scopes: GRAPH_SCOPE,
    account,
  });

  const token = response.accessToken;

  const folderMetadata = await resolveSharedItem(sharePointURL, token);
  const driveId = folderMetadata.parentReference?.driveId;
  // parentReference.siteId is reliably populated for SharePoint/OneDrive for
  // Business drives (and intentionally absent for personal OneDrive).
  // See: https://learn.microsoft.com/en-us/graph/api/resources/itemreference
  const siteId =
    folderMetadata.parentReference?.siteId ||
    folderMetadata.sharepointIds?.siteId;
  const siteData = siteId ? { id: siteId } : {};

  return { folderMetadata, driveId, siteData, token };
};

// Walks the folder tree rooted at (driveId, folderId) and returns every file
// found. This is the expensive, potentially slow step (recursively lists
// every subfolder) -- call it only when the user actually wants to browse/
// attach files (e.g. after clicking "Proceed"), using the driveId/folderId/
// token already obtained from resolveSharePointLink().
export const fetchSharePointFiles = (driveId, folderId, token) =>
  getAllFilesInFolder(driveId, folderId, token);
